import type {
  ApplicationDocument,
  ApplicationSubmissionDocument,
  ProductionDocument,
} from '@/types'
import {
  db,
  typedCollection,
  typedDoc,
  newTypedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  writeBatch,
  serverTimestamp,
  orderBy,
  where,
  deleteField,
} from '@/lib/firebase/firestore'
import type { ApplicationFormValues } from '../types'
import { createUserFacingError } from '@/lib/errors'

const col = () => typedCollection<ApplicationDocument>('applications')
const doc = (id: string) => typedDoc<ApplicationDocument>('applications', id)
const productionDoc = (id: string) => typedDoc<ProductionDocument>('productions', id)
const submissionsCol = () =>
  typedCollection<ApplicationSubmissionDocument>('applicationSubmissions')

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const adminApplicationsService = {
  async getAll(): Promise<Array<ApplicationDocument & { id: string }>> {
    return getCollection(col(), orderBy('order', 'asc'))
  },

  async getById(id: string): Promise<ApplicationDocument | null> {
    return getDocument(doc(id))
  },

  async create(data: ApplicationFormValues): Promise<string> {
    const slug = data.slug || slugify(data.title)
    const existingSlug = await this.getBySlug(slug)
    if (existingSlug) {
      throw createUserFacingError(`An application with slug "${slug}" already exists.`)
    }

    const productionId = data.applicationType === 'production_intake' ? data.productionId : ''
    if (productionId) {
      const production = await getDocument(productionDoc(productionId))
      if (!production) throw createUserFacingError('The linked production could not be found.')
      if (production.intakeApplicationId) {
        throw createUserFacingError('That production already has an intake application linked.')
      }
    }

    const payload = {
      title: data.title,
      description: data.description,
      slug,
      status: data.status,
      isPublished: data.isPublished,
      autoAccept: data.autoAccept ?? false,
      order: data.order,
      applicationType: data.applicationType,
      ...(productionId ? { productionId } : {}),
      ...(data.deadline ? { deadline: new Date(data.deadline) } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = newTypedDoc(col())
    const batch = writeBatch(db)
    batch.set(ref, payload as unknown as ApplicationDocument)
    if (productionId) {
      batch.update(productionDoc(productionId), {
        intakeApplicationId: ref.id,
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
    return ref.id
  },

  async update(id: string, data: Partial<ApplicationFormValues>): Promise<void> {
    // Enforce applicationType immutability once submissions exist. This is the
    // canonical guard — the edit page disables the radio for UX but a stale tab
    // or direct service call would otherwise slip through.
    const existing = await getDocument(doc(id))
    if (!existing) throw createUserFacingError('Application not found.')

    if (data.slug !== undefined) {
      const existingSlug = await this.getBySlug(data.slug)
      if (existingSlug && existingSlug.id !== id) {
        throw createUserFacingError(`An application with slug "${data.slug}" already exists.`)
      }
    }

    if (data.applicationType !== undefined) {
      const currentType = existing.applicationType ?? 'general'
      if (data.applicationType !== currentType) {
        const subs = await getCollection(submissionsCol(), where('applicationId', '==', id))
        if (subs.length > 0) {
          throw createUserFacingError(
            'Cannot change application type after submissions have been received.'
          )
        }
      }
    }

    const nextType = data.applicationType ?? existing.applicationType ?? 'general'
    const requestedProductionId =
      'productionId' in data ? (data.productionId ?? '') : (existing.productionId ?? '')
    const nextProductionId = nextType === 'production_intake' ? requestedProductionId : ''
    if (nextProductionId) {
      const production = await getDocument(productionDoc(nextProductionId))
      if (!production) throw createUserFacingError('The linked production could not be found.')
      if (production.intakeApplicationId && production.intakeApplicationId !== id) {
        throw createUserFacingError('That production already has an intake application linked.')
      }
    }

    const patch: Record<string, unknown> = {
      ...data,
      updatedAt: serverTimestamp(),
    }
    if ('deadline' in data) {
      patch.deadline = data.deadline ? new Date(data.deadline) : deleteField()
    }
    if ('productionId' in data || nextType !== 'production_intake') {
      patch.productionId = nextProductionId || deleteField()
    }

    const batch = writeBatch(db)
    batch.update(doc(id), patch)

    if (existing.productionId && existing.productionId !== nextProductionId) {
      const previousProduction = await getDocument(productionDoc(existing.productionId))
      if (previousProduction?.intakeApplicationId === id) {
        batch.update(productionDoc(existing.productionId), {
          intakeApplicationId: deleteField(),
          updatedAt: serverTimestamp(),
        })
      }
    }
    if (nextProductionId) {
      batch.update(productionDoc(nextProductionId), {
        intakeApplicationId: id,
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  },

  async updateStatus(id: string, status: ApplicationDocument['status']): Promise<void> {
    await updateDocument(doc(id), { status, updatedAt: serverTimestamp() } as unknown as Partial<ApplicationDocument>)
  },

  async updatePublished(id: string, isPublished: boolean): Promise<void> {
    await updateDocument(doc(id), { isPublished, updatedAt: serverTimestamp() } as unknown as Partial<ApplicationDocument>)
  },

  async updateImageUrl(id: string, imageUrl: string): Promise<void> {
    await updateDocument(doc(id), { imageUrl, updatedAt: serverTimestamp() } as unknown as Partial<ApplicationDocument>)
  },

  async updateImageFocus(id: string, x: number, y: number): Promise<void> {
    await updateDocument(doc(id), {
      imageFocusX: x,
      imageFocusY: y,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ApplicationDocument>)
  },

  async storeDeadlineTask(id: string, taskName: string | null): Promise<void> {
    if (taskName) {
      await updateDocument(doc(id), { deadlineTaskName: taskName, updatedAt: serverTimestamp() } as unknown as Partial<ApplicationDocument>)
    } else {
      await updateDocument(doc(id), { deadlineTaskName: deleteField(), updatedAt: serverTimestamp() } as unknown as Partial<ApplicationDocument>)
    }
  },

  async delete(id: string): Promise<void> {
    const application = await getDocument(doc(id))
    if (!application?.productionId) {
      await deleteDocument(doc(id))
      return
    }

    const production = await getDocument(productionDoc(application.productionId))
    const batch = writeBatch(db)
    batch.delete(doc(id))
    if (production?.intakeApplicationId === id) {
      batch.update(productionDoc(application.productionId), {
        intakeApplicationId: deleteField(),
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  },

  async getBySlug(slug: string): Promise<(ApplicationDocument & { id: string }) | null> {
    const results = await getCollection(col(), where('slug', '==', slug))
    return results[0] ?? null
  },

  async getPublished(): Promise<Array<ApplicationDocument & { id: string }>> {
    return getCollection(col(), where('isPublished', '==', true), orderBy('order', 'asc'))
  },

  slugify,
}
